/** @mui/material imports */
import Backdrop from '@mui/material/Backdrop/Backdrop';
import Box from '@mui/material/Box/Box';
import CircularProgress from '@mui/material/CircularProgress';
/** @mui/icons-material imports */
/** React imports */
import React, { createContext, useEffect, useRef, useState } from 'react';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  MutationCache,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
/** App imports */
import Navbar from './Components/Navbar';
import HomeCannotUse from './Components/HomeCannotUse';
import { errorHandler } from './utils/error/error-handler';
import config from './utils/config';
import { getWebWorker } from './utils/load-web-worker';
import AppScopeLoadingComponent from './utils/components/AppScopeLoading';
import { AppAlertComponent } from './utils/components/AppAlert';
import { TessellationFormContextProvider } from './Contexts/TessellationFormContext';
import { WizardContextProvider } from './Contexts/WizardContext';
import { DatasetContextProvider } from './Contexts/DatasetContext';
import { AlertObject, ProjectObject } from './TypeScript/interfaces';
import { JobsResponse, JobStatusTypes } from '@goldspotdiscoveries/atoms';
import { apiUrls } from './TypeScript/process-constants';

const AppRouter = React.lazy(() => {
  return import('./Router/router');
});

interface AppProps {
  user: {
    username: string;
    signInUserSession?: any;
    attributes?: any;
    userDataKey?: string;
  };
  signOut: () => void;
}

export const appContext = createContext({
  appAlerts: [] as AlertObject[] | [],
  setAppAlerts: (value: any): void => {},
  addAppAlert: (value: AlertObject): void => {},
  appLoading: false,
  setAppLoading: (value: boolean): void => {},
  jobs: {} as Record<string, JobsResponse>,
  addJob: (job: JobsResponse): void => {},
  user: {} as {
    username: string;
    signInUserSession?: any;
    attributes?: any;
    userDataKey?: string;
  },
  company: '', // for switch companies
  setCompany: (value: string): void => {},
  cloudFilesOrg: false,
  setCloudFilesOrg: (value: boolean): void => {},
  activeProject: {} as ProjectObject,
  setActiveProject: (value: ProjectObject): void => {},
});

/**
 * Timer to measure inactivity
 * checks for expiration each 5 seconds.
 * session expires after x minutes and warns of logout if y minutes are remaining.
 * x and y are in /src/utils/config.tsx
 */
class IdleTimer {
  timeout: number;
  warned: boolean = false;
  onTimeout;
  interval: NodeJS.Timeout | number = -1;
  warning: () => void = () => {};
  eventHandler: (ev: MouseEvent | KeyboardEvent | Event) => any = (ev) => {};
  timeoutTracker?: NodeJS.Timeout;
  constructor({
    timeout,
    onTimeout,
    onExpired,
    warning,
  }: {
    timeout: number;
    onTimeout: () => void;
    onExpired: () => void;
    warning: () => void;
  }) {
    this.timeout = timeout;
    this.onTimeout = onTimeout;
    this.warning = warning;
    // check for expired session on instantiation
    const expiredTime = parseInt(
      localStorage.getItem('_inactiveTimer') || '0',
      10,
    );
    if (expiredTime > 0 && expiredTime < Date.now()) {
      this.cleanUp();
      onExpired();
      return;
    }
    this.eventHandler = this.updateExpiredTime.bind(this);
    // start tracking 'activity'
    this.tracker();
    // start checking if session expired
    this.startInterval();
  }
  tracker() {
    window.addEventListener('mousemove', this.eventHandler);
    window.addEventListener('scroll', this.eventHandler);
    window.addEventListener('keydown', this.eventHandler);
  }
  cleanUp() {
    localStorage.removeItem('_inactiveTimer');
    clearInterval(this.interval as number);
    window.removeEventListener('mousemove', this.eventHandler);
    window.removeEventListener('scroll', this.eventHandler);
    window.removeEventListener('keydown', this.eventHandler);
  }
  // the event listener for the tracker and cleanup
  updateExpiredTime(ev?: MouseEvent | KeyboardEvent | Event): any {
    if (this.timeoutTracker) {
      clearTimeout(this.timeoutTracker);
    }
    this.timeoutTracker = setTimeout(() => {
      localStorage.setItem(
        '_inactiveTimer',
        `${Date.now() + this.timeout * 60 * 1000}`,
      );
    }, 300);
    return;
  }
  startInterval() {
    const warningPeriod: number = config.session_inactive_warning * 60 * 1000;
    this.updateExpiredTime();
    this.interval = setInterval(() => {
      const expiredTime = parseInt(
        localStorage.getItem('_inactiveTimer') || '0',
        10,
      );
      if (expiredTime < Date.now()) {
        if (this.onTimeout) {
          this.cleanUp();
          this.onTimeout();
        }
      } else if (!this.warned && expiredTime - Date.now() <= warningPeriod) {
        if (this.warning) {
          this.warned = true;
          this.warning();
        }
      }
    }, 5000);
  }
}

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, { meta }) => {
      errorHandler(error as Error);
      // Show error message
      if (meta?.errorMessage) {
        console.error(meta?.errorMessage);
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      errorHandler(error as Error);
      // Show error message
      if (mutation.meta?.errorMessage) {
        console.error(mutation.meta?.errorMessage);
      }
    },
  }),
});

const App: React.FC<AppProps> = ({ user, signOut }): JSX.Element => {
  // High level state to control the Side Drawer via sibbling Navbar
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [appAlerts, setAppAlerts] = useState<AlertObject[] | []>([]);
  const [appLoading, setAppLoading] = useState<boolean>(false);
  const [company, setCompany] = useState<string>(
    user.signInUserSession.idToken.payload['custom:company'],
  ); //default company
  const [cloudFilesOrg, setCloudFilesOrg] = useState<boolean>(false);
  const [jobs, setJobs] = useState<Record<string, JobsResponse>>({});
  const [monitorJobWorker, setMonitorJobWorker] = useState<Worker>();
  const [isTimeout, setIsTimeout] = useState<boolean>(false);
  const [activeProject, setActiveProject] = useState<ProjectObject>({});
  const jobsRef = useRef<Record<string, JobsResponse>>(jobs);
  const { t, i18n } = useTranslation();

  let cannotUseApp = false; // Need to eveluate this once
  /**
   * Track session expired from inactivity
   */
  useEffect(() => {
    const inactiveTimeoutMinutes = config.session_inactive_timeout;
    const timer = new IdleTimer({
      timeout: inactiveTimeoutMinutes,
      onTimeout: () => {
        // when user is in app and is inactive for an hour
        setIsTimeout(true);
        signOut();
      },
      onExpired: () => {
        // when user opens app and the timer is expired
        setIsTimeout(true);
        signOut();
      },
      warning: () => {
        addAppAlert({
          severity: 'warning',
          message: t('ALERT_INACTIVE_TIMEOUT_WARNING'),
        });
      },
    });
    return () => {
      timer.cleanUp();
    };
  }, []);

  /**
   * get/set app language at app init (changes handled in LanguageSwitcher component)
   */
  useEffect(() => {
    const supportsLocalStorage: boolean = window.hasOwnProperty('localStorage');
    const selectedLanguage: string =
      window.localStorage?.getItem(`${user.userDataKey}.selectedLanguage`) ||
      '';
    const defaultLanguage: string = i18n.options.lng || 'en';
    if (supportsLocalStorage && !selectedLanguage) {
      window.localStorage.setItem(
        `${user.userDataKey}.selectedLanguage`,
        defaultLanguage,
      );
    } else if (user && supportsLocalStorage && selectedLanguage) {
      i18n.changeLanguage(selectedLanguage);
    }
  }, [user]);

  /**
   * Maintain a React Ref to the jobs context
   */
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  /**
   * Runs when App Component mounts
   * @returns cleanup function to run when App Component unmounts
   */
  useEffect(() => {
    let workerUrl: string = '';
    // get the job manager web worker
    getWebWorker('/jobs-manager.js')
      .then(({ worker, workerUrl }) => {
        // set the message handler for the web worker
        worker.onmessage = handleMonitorJobMessage;
        // set the url in order to be able to revoke it in the cleanup function
        workerUrl = workerUrl;
        // add web worker to state
        setMonitorJobWorker(worker);
      })
      .catch((err) => {
        errorHandler(err);
      });
    return () => {
      // disconnect the web worker
      monitorJobWorker?.terminate();
      // release the virtual url to the web worker script
      URL.revokeObjectURL(workerUrl);
    };
  }, []);

  const handleCloseAlert = (): void => {
    appAlerts.shift(); // Removes the first Alert (closing one Aler at a time)
    setAppAlerts([...appAlerts]);
  };

  const addAppAlert = (alert: AlertObject): void => {
    setAppAlerts([...appAlerts, alert]);
  };

  const signOutAndClearExpiry = () => {
    localStorage.removeItem('_inactiveTimer');
    signOut();
  };

  /**
   * When web worker posts a message to app, update jobs context if job has
   * a different status from the one in context.
   * Web worker will return the job object when it detects an updated status
   * @param message message from web worker
   */
  const handleMonitorJobMessage = (
    message: MessageEvent<JobsResponse | undefined>,
  ): void => {
    if (message.data) {
      const updatedJob: JobsResponse = message.data;
      const existingJobs: Record<string, JobsResponse> = jobsRef.current;
      if (
        existingJobs[updatedJob.id] &&
        existingJobs[updatedJob.id].status !== updatedJob.status
      ) {
        // job status has updated; handle change here;
        const updatedJobs: Record<string, JobsResponse> = Object.assign(
          {},
          existingJobs,
        );
        updatedJobs[updatedJob.id] = updatedJob;
        setJobs(updatedJobs);
        if (updatedJob.status && updatedJob.status === JobStatusTypes.done) {
          addAppAlert({
            severity: 'info',
            message: `Job done: "${updatedJob.description || updatedJob.id}"`,
          });
        } else if (updatedJob.status && updatedJob.status === 'cancelled') {
          addAppAlert({
            severity: 'info',
            message: `Job cancelled: "${
              updatedJob.description || updatedJob.id
            }"`,
          });
        }
      }
    } else {
      // web worker errored-out
    }
  };

  /**
   * Make a copy of jobs, extend it, then replace the original
   */
  const addJob = (job: JobsResponse): void => {
    const updatedJobs = {
      ...jobs,
      [job.id]: job,
    };
    setJobs(updatedJobs);
    // send job to web worker to monitor
    monitorJob(job);
  };

  /**
   * Pass a job to the job manager (Web Worker) to watch for status updates;
   */
  const monitorJob = (job: JobsResponse): void => {
    const token: string = user.signInUserSession.getIdToken().getJwtToken();
    monitorJobWorker?.postMessage({
      job,
      token,
      apiHostname: apiUrls.projects,
    });
  };

  // User must belong to a company defined it the JWT (['custom:company']). This is required by the API Backends
  if (!user.signInUserSession?.idToken?.payload?.['custom:company']) {
    cannotUseApp = true;
  }

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <Box sx={{ display: 'flex' }}>
          <Navbar
            setIsDrawerOpen={setIsDrawerOpen}
            isDrawerOpen={isDrawerOpen}
            user={user}
            company={company}
            signOut={signOutAndClearExpiry}
          />
          {!isTimeout && cannotUseApp ? (
            <HomeCannotUse />
          ) : (
            <appContext.Provider
              value={{
                appAlerts,
                setAppAlerts,
                addAppAlert,
                jobs,
                addJob,
                appLoading,
                setAppLoading,
                user,
                company,
                setCompany,
                cloudFilesOrg,
                setCloudFilesOrg,
                activeProject,
                setActiveProject,
              }}
            >
              <TessellationFormContextProvider>
                <DatasetContextProvider>
                  <WizardContextProvider>
                    <React.Suspense fallback={<AppScopeLoadingComponent />}>
                      <AppRouter
                        user={user}
                        setIsDrawerOpen={setIsDrawerOpen}
                        isDrawerOpen={isDrawerOpen}
                      />
                    </React.Suspense>
                  </WizardContextProvider>
                </DatasetContextProvider>
              </TessellationFormContextProvider>
            </appContext.Provider>
          )}
        </Box>
        <Backdrop sx={{ color: '#fff', zIndex: '2000' }} open={appLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
        {appAlerts.length > 0 && (
          <AppAlertComponent
            appAlert={appAlerts[0]}
            handleCloseAlert={handleCloseAlert}
          />
        )}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </BrowserRouter>
  );
};
export default App;
